import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import { memo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { GiornoFascia, StatoDati } from '../../hooks/usePresenzeGiorni';
import {
  ColonnaGiorno,
  useColonneGiorni,
} from '../../pages/Default/hooks/useColonneGiorni';
import {
  GiornoFasciaNegozio,
  usePresenzeFascia,
} from '../../pages/Default/hooks/usePresenzeFascia';
import {
  LARGHEZZA_MINIMA_CELLA,
  TestoChip,
} from '../../utility_objects/PresenzeUtils';
import { CellaPresenzeSkeleton } from './CellaCliccabile';
import { CellaPresenze } from './CellaPresenze';

// Sotto questa larghezza della colonna delle ore l'etichetta è solo l'icona.
const LARGHEZZA_MINIMA_ETICHETTA = 70;

// Con le chip l'etichetta sta in alto, all'altezza della prima riga (stesso padding
// superiore della cella); con tutto in errore resta centrata come la scritta. Come
// in FOCUS CLOUD.
const EtichettaPresenze = memo(function EtichettaPresenze({
  larghezza,
  inAlto,
}: {
  larghezza: number;
  inAlto: boolean;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: inAlto ? 'flex-start' : 'center',
        pt: inAlto ? 0.5 : 0,
        justifyContent: 'center',
        minWidth: 0,
        overflow: 'hidden',
        px: 0.5,
        borderRight: '1px solid var(--divider-color)',
      }}
    >
      {larghezza < LARGHEZZA_MINIMA_ETICHETTA ? (
        // Tooltip MUI e non title: su tablet si apre con la pressione prolungata.
        <Tooltip title="Presenze">
          <Box
            component="span"
            role="img"
            aria-label="Presenze"
            sx={{ display: 'flex' }}
          >
            <PeopleAltOutlinedIcon fontSize="small" color="action" />
          </Box>
        </Tooltip>
      ) : (
        <Typography variant="caption" color="text.secondary" noWrap>
          Presenze
        </Typography>
      )}
    </Box>
  );
});

// Durante la navigazione lo Scheduler viene smontato: finché non compaiono le celle
// nuove valgono le ultime misure, se il numero di giorni è lo stesso. Altrimenti
// null, e la fascia mostra lo Skeleton su colonne uguali.
const getLarghezze = (
  giorni: GiornoFascia[],
  colonne: ColonnaGiorno[] | null
): number[] | null =>
  colonne && colonne.length === giorni.length
    ? colonne.map((colonna) => colonna.larghezza)
    : null;

// Colonna delle ore + un giorno per colonna + colonna finale, che con le misure
// assorbe il padding della scrollbar dell'intestazione.
const getColonneGriglia = (
  sinistra: number,
  destra: number,
  quantiGiorni: number,
  larghezze: number[] | null
): string =>
  larghezze
    ? `${sinistra}px ${larghezze
        .map((larghezza) => `${larghezza}px`)
        .join(' ')} minmax(0, 1fr)`
    : `${sinistra}px repeat(${quantiGiorni}, minmax(0, 1fr)) ${destra}px`;

// Cella di un giorno: in Agenda sempre la cella a tutta larghezza, con la data;
// altrimenti lo Skeleton finché la colonna non è misurata (cambio di vista) e niente
// per i giorni chiusi a 0px della vista Lavorativo.
const CellaGiorno = ({
  giorno,
  statoAssenze,
  testo,
  agenda,
  larghezza,
  bordo,
}: {
  giorno: GiornoFasciaNegozio;
  statoAssenze: StatoDati;
  testo: TestoChip;
  agenda: boolean;
  /** Larghezza della colonna; null = non ancora misurata. */
  larghezza: number | null;
  bordo: boolean;
}): JSX.Element => {
  if (!agenda && larghezza === null)
    return <CellaPresenzeSkeleton bordo={bordo} />;
  if (!agenda && larghezza !== null && larghezza < LARGHEZZA_MINIMA_CELLA)
    return <Box sx={{ minWidth: 0 }} />;
  return (
    <CellaPresenze
      giorno={giorno}
      statoAssenze={statoAssenze}
      testo={testo}
      orarioNegozio={giorno.orarioNegozio}
      conData={agenda}
      bordo={bordo}
    />
  );
};

/**
 * Fascia Presenze dell'agenda negozio, fra la barra delle viste e il calendario:
 * una cella per giorno visibile allineata alle colonne di Syncfusion (misurate da
 * useColonneGiorni), con una chip per ogni persona, in servizio o assente. In
 * «Presenze» le chip hanno il solo username, in «Presenze totali» il testo completo
 * (con «Orario negozio» per chi lavora con l'orario del punto vendita: gli orari
 * arrivano con i giorni da usePresenzeFascia). Alta almeno 48px e quanto
 * serve perché le chip si vedano tutte: tutte le celle sono alte come la più alta e
 * il calendario sotto si accorcia (useSchedulerLayoutRefresh).
 * Le colonne della griglia sono sempre etichetta + giorni + colonna finale, anche
 * con lo Skeleton; con tutte le chiamate in errore una sola scritta prende il posto
 * delle celle dei giorni e della colonna finale (le colonne sono esplicite,
 * l'etichetta non si sposta). In Agenda è una sola cella a tutta larghezza.
 */
export const FasciaPresenze = memo(function FasciaPresenze(): JSX.Element {
  const { view, presenzeModo } = useSelector(
    (state: RootState) => state.bluBookingDefaultCalendar
  );
  const { giorni, statoAssenze, tuttoInErrore } = usePresenzeFascia();
  const fasciaRef = useRef<HTMLDivElement>(null);
  const agenda = view === 'Agenda';
  const testo: TestoChip = presenzeModo === 'totali' ? 'completo' : 'breve';
  const { sinistra, destra, colonne } = useColonneGiorni(fasciaRef, agenda);

  const larghezze = agenda ? null : getLarghezze(giorni, colonne);

  return (
    <Box
      ref={fasciaRef}
      role="group"
      aria-label="Presenze"
      sx={{
        // Alta quanto la cella più alta, senza tetto.
        minHeight: 48,
        flexShrink: 0,
        display: 'grid',
        // Agenda: nessuna intestazione a cui allinearsi, una sola cella.
        gridTemplateColumns: agenda
          ? 'minmax(0, 1fr)'
          : getColonneGriglia(sinistra, destra, giorni.length, larghezze),
        overflow: 'hidden',
        backgroundColor: 'var(--base-foreground-color)',
      }}
    >
      {!agenda && (
        <EtichettaPresenze larghezza={sinistra} inAlto={!tuttoInErrore} />
      )}
      {tuttoInErrore ? (
        // Piccola e centrata sui giorni, come in FOCUS CLOUD.
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{
            gridColumn: agenda ? '1 / -1' : '2 / -1',
            alignSelf: 'center',
            textAlign: 'center',
            px: 1,
          }}
        >
          Presenze non disponibili
        </Typography>
      ) : (
        giorni.map((giorno, i) => (
          <CellaGiorno
            key={giorno.giorno}
            giorno={giorno}
            statoAssenze={statoAssenze}
            testo={testo}
            agenda={agenda}
            larghezza={larghezze?.[i] ?? null}
            bordo={!agenda && i > 0}
          />
        ))
      )}
      {/* Colonna finale, vuota. */}
      {!agenda && !tuttoInErrore && <Box />}
    </Box>
  );
});
